import { EventObject } from '@xstate/store-react';
import { AppRoute } from '../../../enums/appRoutes';
import { StoreContext } from '../../../models/store/context';
import { QuoteStatus } from '../../../models/store/quoteStatus';
import { QuoteResponse } from '../../../models/services/api/postQuote';
import { saveSession } from '../../../services/session/save';
import { StoreEvent } from '../../../enums/storeEvent';
import { log } from '@domgen/shared/utils/log';

/**
 * Stores quote response data and computed offer details for quote/confirmation views.
 *
 * Chooses payment method preference for display values, navigates to quote,
 * and persists the updated context to session storage.
 *
 * @param context Current store context.
 * @param event Event containing quote response data.
 * @param enqueue Store enqueue helpers used for navigation emission.
 * @returns Updated context with quote, confirmation offer data, and GA fields.
 *
 * @todo this currently only support the first contract in the quote response, but we may need to support multiple contracts in the future. Also this is currently not taking into consideration other products that may be added to the quote response, such as extended warranty or other add-ons. We may need to update this logic to handle those cases in the future.
 *
 */
export const setQuote = <T extends EventObject = EventObject>(
  context: StoreContext,
  event: T & { data: QuoteResponse },
  enqueue: {
    effect: (effect: () => Promise<void>) => void;
    emit: { navigate: (payload: { path: AppRoute; replace?: boolean }) => void };
  },
): StoreContext => {
  log.debug({ message: StoreEvent.SET_QUOTE, data: event.data });
  const quote = event.data;

  if (!quote?.offer?.contracts || quote.offer.contracts.length === 0) {
    log.error({ message: 'No contracts found in quote', data: quote });
    enqueue.emit.navigate({ path: AppRoute.ERROR });
    return context;
  }

  const applianceCare = quote?.offer?.contracts[0]?.contractAttributes.monthly_subs;
  const replacementCare = quote?.offer?.contracts[0]?.contractAttributes.fixed_term_single_payment;

  if (!applianceCare && !replacementCare) {
    log.error({ message: 'No offer data found in quote', data: quote });
    enqueue.emit.navigate({ path: AppRoute.ERROR });
    return context;
  }

  log.debug({ message: 'Computed offer data for quote', data: { applianceCare, replacementCare } });

  const offerData = replacementCare
    ? {
        replacementCare,
        startDate: quote?.offer.contracts[0]?.contractAttributes.effectiveDate?.split('T')[0],
        endDate: quote?.offer.contracts[0]?.contractAttributes.contractEndDate,
        netFirstPaymentAmount:
          replacementCare.cc?.netFirstPaymentAmount || replacementCare.ach?.netFirstPaymentAmount,
        offerReference: quote?.offer.offerReference,
      }
    : {
        applianceCare,
        startDate: quote?.offer.contracts[0]?.contractAttributes.effectiveDate?.split('T')[0],
        endDate: quote?.offer.contracts[0]?.contractAttributes.contractEndDate,
        netFirstPaymentAmount:
          applianceCare?.cc?.netFirstPaymentAmount || applianceCare?.ach?.netFirstPaymentAmount,
        netFirstPaymentAmountAfterPromotionalPeriod:
          applianceCare?.cc?.discountProfileAttributes
            ?.netFirstPaymentAmountAfterPromotionalPeriod ||
          applianceCare?.ach?.discountProfileAttributes
            ?.netFirstPaymentAmountAfterPromotionalPeriod,
        discountCode: applianceCare
          ? [
              applianceCare.cc?.discountProfileAttributes?.ccDiscountCodeApplied,
              applianceCare.ach?.discountProfileAttributes?.achDiscountCodeApplied,
            ]
              .filter((code): code is string => !!code && code.toString().trim().length > 0)
              .join(' | ')
          : '',
        offerReference: quote?.offer.offerReference,
      };

  log.debug({ message: 'Computed offer data for quote', data: offerData });

  enqueue.emit.navigate({ path: AppRoute.QUOTE });

  const updatedContext = {
    ...context,
    quote: {
      status: QuoteStatus.READY,
      data: quote,
    },
    confirmation: {
      ...context.confirmation,
      offerData,
    },
    gaEventData: {
      ...context.gaEventData,
      netFirstPaymentAmount: offerData.netFirstPaymentAmount,
    },
  };

  saveSession(updatedContext);

  return updatedContext;
};
